import React, {useCallback, useEffect, useRef} from "react";
import { createMuiTheme } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/styles";
import MomentUtils from "@date-io/moment";
import { MuiPickersUtilsProvider} from '@material-ui/pickers';
import { Redirect, Route, Switch } from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import { IntlProvider } from "react-intl";
import "./assets/vendors/style";
import orangeTheme from "./assets/themes/orangeTheme";
import AppLocale from "./lngProvider";
import LanguageProvider from './providers/LanguageProvider';

import SignIn from "./containers/SignIn";
import RegisterTrial from "./containers/RegisterTrial";
import Error404 from './containers/Error404';
import { setInitUrl, userSignOut } from "./actions/Auth";
import { isAuthenticated } from './utils/IsAuthenticated';
import {
  checkPasswordExpire,
  isPasswordExpired,
  showPasswordExpiredAlert,
  PASSWORD_EXPIRE_CHECK_INTERVAL
} from './utils/PasswordExpire';
import AppLayout from "./containers/AppLayout";
import { PdfInitiator } from "utils/pdf/PdfInitiator";
import ShareQuotationPdf from "./containers/ShareQuotationPdf";

const pdfInitiator = new PdfInitiator();
pdfInitiator.init();

const RestrictedRoute = ({component: Component, authUser, ...rest}) =>
  <Route
    {...rest}
    render={props =>
      authUser !== null
        ? <Component {...props} />
        : <Redirect
          to={{
            pathname: '/auth/signin',
            state: {from: props.location}
          }}
        />}
  />;

const App = (props) => {
  const dispatch = useDispatch();
  const {themeColor, locale} = useSelector(({settings}) => settings);
  const {authUser, initURL} = useSelector(({auth}) => auth);
  const {translate} = useSelector(({i18n}) => i18n);
  const {match, location} = props;
  const checkTimeout = isAuthenticated();
  const isShareRoute = location?.pathname?.startsWith("/share/");

  //อ่านค่าภาษาล่าสุดโดยไม่ให้ effect ตรวจสอบรหัสผ่านทำงานซ้ำตอนเปลี่ยนภาษา
  const translateRef = useRef(translate);
  translateRef.current = translate;

  useEffect(() => {
    window.__MUI_USE_NEXT_TYPOGRAPHY_VARIANTS__ = true;

    if (initURL === '') {
      dispatch(setInitUrl(props.history.location.pathname));
    }

    document.body.classList.add(themeColor);
  }, [dispatch, initURL, props.history.location.pathname, themeColor]);

  //กรณีใช้งานอยู่: ถ้ารหัสผ่านหมดอายุให้ clear session แล้วกลับไปหน้า login พร้อมแจ้งเตือน
  const company = authUser ? authUser.company : null;
  const usernameSignedIn = authUser ? authUser.username : null;

  //กัน popup ซ้ำ กรณีเช็คพร้อมกันหลายรอบ (เปลี่ยนหน้า + interval)
  const isHandlingExpiredRef = useRef(false);

  const verifyPasswordExpire = useCallback(async () => {
    if (isShareRoute || !company || !usernameSignedIn || isHandlingExpiredRef.current) return;

    const passwordExpire = await checkPasswordExpire(company, usernameSignedIn);

    if (!isPasswordExpired(passwordExpire) || isHandlingExpiredRef.current) return;

    isHandlingExpiredRef.current = true;

    //เก็บ token ไว้ก่อน เพราะ userSignOut จะลบออกจาก localStorage ก่อนดึง Company Config
    const accessToken = localStorage.getItem('x-access-token');

    dispatch(userSignOut());
    showPasswordExpiredAlert(company, translateRef.current, { isSignedIn: true, accessToken });
  }, [dispatch, company, usernameSignedIn, isShareRoute]);

  //เช็คทุกครั้งที่เปลี่ยนหน้า เพื่อไม่ต้องรอรอบ interval หรือให้ผู้ใช้ refresh เอง
  useEffect(() => {
    verifyPasswordExpire();
  }, [verifyPasswordExpire, location.pathname]);

  //เช็คเป็นระยะ เผื่อผู้ใช้เปิดหน้าเดิมค้างไว้โดยไม่เปลี่ยนหน้า
  useEffect(() => {
    const timer = setInterval(verifyPasswordExpire, PASSWORD_EXPIRE_CHECK_INTERVAL);
    return () => clearInterval(timer);
  }, [verifyPasswordExpire]);

  let applyTheme = createMuiTheme(orangeTheme);

  if(!isShareRoute && checkTimeout === false){
    dispatch(userSignOut())
    return ( <Redirect to={'/auth/signin'}/> )
  }
  
  if (location.pathname === '/') {
    if (authUser === null) {
      return ( <Redirect to={'/auth/signin'}/> );
    } else if (initURL === '' || initURL === '/' || initURL === '/auth/signin') {
      return ( <Redirect to={'/app/dashboard'}/> );
    } else {
      return ( <Redirect to={initURL}/> );
    }
  }
  
  const currentAppLocale = AppLocale[locale.locale];
  return (
    <ThemeProvider theme={applyTheme}>
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <LanguageProvider>
          <IntlProvider
            locale={currentAppLocale.locale}
            messages={currentAppLocale.messages}>
              <div className="app-main">
                <Switch>
                  <Route path='/share/quotation' component={ShareQuotationPdf}/>
                  <RestrictedRoute path={`${match.url}app`} authUser={authUser}
                                  component={AppLayout}/>
                  <Route path='/auth/signin' component={SignIn}/>
                  <Route path='/auth/register-trial' component={RegisterTrial}/>
                  <Route component={Error404}/>
                </Switch>
              </div>
          </IntlProvider>
        </LanguageProvider>
      </MuiPickersUtilsProvider>
    </ThemeProvider>
  );
};

export default App;